// external
import classNames from "classnames";

// components
import CustomTextInput from "components/partials/custom-text-input/custom-text-input";
import CustomButton from "components/partials/custom-button/custom-button";

// helpers
import { formatFloats } from "../helpers/published-model-helpers";

// assets
import Icons from "../../../assets/icons";

/**
 * Results Table Renderers
 *
 * This module contains helper functions for rendering different types of table cells
 * and components used in FAO aid results tables. It provides consistent rendering logic
 * for input cells, display cells, and table headers with various formatting options.
 */

export const UNIT_SYMBOLS = {
  percent: "%",
  currency: "$",
};

/**
 * Retrieves the corresponding value from last year's data for comparison
 *
 * @param {Array} lastYearData - Array of last year's data objects
 * @param {string} group - The group identifier to match
 * @param {string} headerKey - The key of the value to retrieve
 * @returns {*} The last year's value or null if not found
 */
export const getLastYearValue = (lastYearData, group, headerKey) => {
  const lastYearItem = lastYearData.find((cur) => cur.group === group);
  return lastYearItem ? lastYearItem[headerKey] : null;
};

/**
 * Renders an editable input cell with number input and unit symbol
 *
 * Features:
 * - Custom number input with step increment
 * - Unit symbol positioned absolutely within the input
 * - Last year comparison value displayed alongside
 * - Proper event handling for value changes
 * - Responsive layout with flex containers
 *
 * @param {Object} props - Rendering parameters
 * @param {Object} props.header - Column header configuration
 * @param {number} props.headerIndex - Index of current header
 * @param {Object} props.item - Data item being rendered
 * @param {string} props.inputStep - Step value for number input
 * @param {Array} props.tableHeaders - All table headers for border logic
 * @param {Function} props.handleInputChange - Callback for input changes
 * @param {boolean} props.showLastYear - Whether to show last year values
 * @param {Array} props.lastYearData - Last year's data for comparison
 * @returns {JSX.Element} Table cell with input field
 */
export const renderInputCell = ({
  header,
  headerIndex,
  item,
  inputStep,
  tableHeaders,
  handleInputChange,
  showLastYear,
  lastYearData,
}) => {
  const value = item[header.key];
  const group = item.group;
  const unit = UNIT_SYMBOLS[header.unit];
  const lastYearValue = getLastYearValue(lastYearData, group, header.key);

  return (
    <td
      key={`${group}-${header.key}`}
      className={classNames("w-full", {
        "border-r": tableHeaders.length - 1 !== headerIndex,
      })}
    >
      <div className="flex items-center  justify-between">
        {/* Input container with relative positioning for unit symbol */}
        <div className="relative flex min-w-0 flex-1 items-center">
          <div className="relative w-full">
            <CustomTextInput
              id={`${header.key}_${group}`}
              step={inputStep}
              required={true}
              inputWrapperCustomClass="w-full border border-blue-02 m-1"
              inputCustomClass="h-9 px-2 pr-8 bg-transparent border-none !w-full"
              onChange={(e) =>
                handleInputChange({
                  nonModeledItem: item,
                  newValue: e.target.value,
                  field: header.key,
                })
              }
              type="number"
              value={value}
            />
            {/* Unit symbol positioned absolutely within input */}
            {unit && (
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-04">
                {unit}
              </span>
            )}
          </div>
        </div>
        {/* Last year comparison value */}
        {showLastYear && lastYearValue !== null && (
          <span className="mr-1 w-8 flex-shrink-0 rounded bg-gray-100 p-1 text-center text-sm font-medium">
            {lastYearValue}
          </span>
        )}
      </div>
    </td>
  );
};

const renderResetButton = (handleReset, customClass) => {
  return (
    <CustomButton
      customClass={customClass}
      iconBefore={
        <Icons.Reset customClass="h-4 w-4 rotate-45 text-blue-01 size-2 hover:text-blue-03 transition-all" />
      }
      onClick={handleReset}
      aria-label="Reset value"
    />
  );
};
/**
 * Renders a regular (non-editable) table cell with formatted content
 *
 * Features:
 * - Automatic value formatting based on unit type
 * - Reset button for row headers when in reset mode
 * - Conditional styling for different cell types
 * - Proper text alignment and spacing
 *
 * @param {Object} props - Rendering parameters
 * @param {Object} props.header - Column header configuration
 * @param {number} props.headerIndex - Index of current header
 * @param {Object} props.item - Data item being rendered
 * @param {Array} props.tableHeaders - All table headers for border logic
 * @param {boolean} props.resetToggle - Whether reset functionality is enabled
 * @param {boolean} props.isEditable - Whether the table is in editable mode
 * @param {Function} props.handleReset - Callback for reset action
 * @returns {JSX.Element} Table cell with formatted content
 */
export const renderRegularCell = ({
  header,
  headerIndex,
  item,
  tableHeaders,
  resetToggle,
  isEditable,
  handleReset,
}) => {
  const value = item[header.key];
  const group = item.group;

  // Format display value based on whether it's a row header or data cell
  const displayValue = header.isRowHeader
    ? value // Row headers show raw text
    : formatFloats(value, header.unit); // Data cells get formatted

  const shouldShowResetButton = header.isRowHeader && resetToggle;

  return (
    <td
      key={`${group}-${header.key}`}
      className={classNames("text-left text-gray-05", {
        "border-r": tableHeaders.length - 1 !== headerIndex,
        "font-semibold": header.isRowHeader,
        "p-3": !resetToggle,
      })}
    >
      <div className="flex items-center">
        {/* Reset button for individual rows */}
        {shouldShowResetButton &&
          renderResetButton(() => handleReset(item), "!px-2")}
        {/* Cell content with conditional padding */}
        <p className={classNames({ "pl-2": isEditable })}>{displayValue}</p>
      </div>
    </td>
  );
};

/**
 * Renders table header content with conditional reset functionality
 *
 * Logic:
 * - Shows header text if headerVisible is true
 * - Shows reset all button for row headers when in reset mode
 * - Returns empty string for other cases
 *
 * @param {Object} item - Header item configuration
 * @param {boolean} resetToggle - Whether reset functionality is enabled
 * @param {Function} handleResetAll - Callback for reset all action
 * @returns {string|JSX.Element} Header content
 */
export const renderTableHeader = (item, resetToggle, handleResetAll) => {
  if (item.headerVisible) {
    return item.header;
  }
  if (item.isRowHeader && resetToggle) {
    return renderResetButton(() => handleResetAll(), "!p-0");
  }
  return "";
};
